import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { AllocationsContext, AllocationService } from 'src/app/_shared/services/allocations/detail/allocation.service';
import { CompteDto, JournalDto } from 'src/graphql/generated-types';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { JournalLabelPipe } from 'src/app/_shared/pipes/journal-label.pipe';
import {
    AccountingVirtualWalletAllocationLabelPipe
} from 'src/app/board/accounting/pipes/accounting-virtual-wallet-allocation-label.pipe';
import { Select } from '@ngxs/store';
import {
    AccountingVirtualWalletAllocationState
} from 'src/app/board/accounting/stores/accounting-virtual-wallet/accounting-virtual-wallet-allocation-state';
import {
    AnalyticalPlanAndSections,
    AnalyticalPlanState
} from 'src/app/_shared/stores/analytical-plan/analytical-plan.state';
import isEqual from 'lodash-es/isEqual';
import { AttachLabelPipe } from 'src/app/_shared/pipes/attach-label.pipe';
import { UserState } from 'src/app/_user/store/user/user.state';
import { ReferenceYearsState } from 'src/app/_shared/stores/reference-years/reference-years.state';

@Component({
    selector: 'app-shared-allocation-informations',
    templateUrl: './shared-allocation-informations.component.html',
    styleUrls: ['./shared-allocation-informations.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedAllocationInformationsComponent extends BaseComponent implements OnInit {

    @Select(AccountingVirtualWalletAllocationState.journalsList) journalsList$: Observable<JournalDto[]>;

    @Select(AccountingVirtualWalletAllocationState.accountsList) accountsList$: Observable<CompteDto[]>;

    @Select(AnalyticalPlanState.analyticalPlansAndSections) analyticalPlansAndSections$: Observable<AnalyticalPlanAndSections[]>;

    isAutomaticAllocation = false;

    allocation$ = this.allocationService.allocation$;

    reportDatasource$ = this.allocationService.reportDatasource$;

    journalsList: JournalDto[] = [];
    accountsList: CompteDto[] = [];

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly graphqlService: GraphqlService,
                private readonly allocationService: AllocationService) {
        super(cdr);
    }

    get type(): string {
        return this.allocationService.type;
    }

    get beneficiariesConcernedCount(): number {
        return this.reportDatasource$.getValue()?.nbOfElements || 0;
    }

    get isAllocationTypeArticle(): boolean {
        return this.type === AllocationsContext.ART;
    }

    get isAllocationTypeVirtualWallet(): boolean {
        return this.type === AllocationsContext.PMV;
    }

    get displayHideAllocation(): boolean {
        return this.snapshot(UserState.modulesList).some(m => ['DIRECTO', 'PORTAIL'].includes(m));
    }

    getReferenceYearLabel(id: number): string {
        const referenceYear = this.snapshot(ReferenceYearsState.referenceYearsList).find(({Id}) => Id === id);
        return referenceYear?.Code || '';
    }

    getJournalLabel(id: number): string {
        return JournalLabelPipe.format(this.journalsList.find(j => j.Id === id));
    }

    getVirtualWalletLabel(id: number): string {
        return AccountingVirtualWalletAllocationLabelPipe.format(this.accountsList.find(j => j.Id === id));
    }

    getAccountLabel(id: number): string {
        return AttachLabelPipe.format(this.accountsList.find(j => j.Id === id));
    }

    ngOnInit(): void {
        if (this.isAllocationTypeVirtualWallet) {
            this.watch$ = combineLatest([
                this.journalsList$,
                this.accountsList$
            ]).pipe(
                distinctUntilChanged(isEqual),
                tap(([journal, account]) => {
                    this.journalsList = journal || [];
                    this.accountsList = account || [];
                })
            );
        }

        // Le datasource ne change pas une fois instancié (si l'attribution a été exécutée),
        // On se base sur le loader pour savoir quand les modifications vont pouvoir être récupérées
        this.watch$ = this.reportDatasource$.pipe(
            switchMap((datasource) => datasource?.loading?.change$ || of(null)),
            tap(() => this.cdr.markForCheck())
        );

        // L'exécution automatique ne concerne actuellement que les attributions de subvention et PMV
        this.watch$ = this.allocationService.automatedExecution$?.pipe(
            filter(() => [AllocationsContext.PMV, AllocationsContext.ATB].some(e => e === this.type)),
            tap(automatedExecution => this.isAutomaticAllocation = automatedExecution?.IsExecutionAuto)
        );
    }
}
